import { useEffect, useState } from 'react';
import { fetchBridgeRoutes } from '../lib/bridgeApi';

// useBridgeChains
//
// Fetch GET /bridge/routes once on mount and reshape it into the `pairs`
// array BridgeForm consumes:
//
//   { src: {id}, dst: {id}, supportedKStables: [...], feeBps, gasFeeRawWei,
//     minConfirmations, estimatedRelaySeconds, decimals,
//     kStables: { <symbol>: { srcKStableAddress, dstKStableAddress,
//                              srcBridgeSenderAddress, dstBridgeReceiverAddress,
//                              protocolFeeBps, gasFeeRawWei } } }
//
// `feeBps` / `gasFeeRawWei` at the top level mirror the currently-selected
// kUSD entry (phase 1 is single-symbol) purely so BridgeForm's existing
// summary math keeps working without threading `symbol` through every call
// site; BridgeForm still looks up `activePair.kStables[symbol]` directly for
// the addresses it needs to build calldata.
//
// Memoised at the module level so navigating between pages doesn't refetch.
// Returns { pairs, loading, error, reload }.

let _cached = null;

function toPairs(routes) {
  return (routes || []).map((r) => {
    const symbols = Array.isArray(r.supportedKStables) ? r.supportedKStables : [];
    const first = symbols[0];
    const firstInfo = first ? r.kStables?.[first] : null;
    return {
      src: { id: r.srcChainId },
      dst: { id: r.dstChainId },
      supportedKStables: symbols,
      kStables: r.kStables || {},
      decimals: r.decimals ?? 6,
      minConfirmations: r.minConfirmations ?? 3,
      estimatedRelaySeconds: r.estimatedRelaySeconds ?? 120,
      feeBps: firstInfo?.protocolFeeBps ?? 5,
      gasFeeRawWei: firstInfo?.gasFeeRawWei ?? '0',
    };
  });
}

export default function useBridgeChains() {
  const [pairs, setPairs] = useState(_cached?.pairs || []);
  const [loading, setLoading] = useState(!_cached);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const res = await fetchBridgeRoutes();
    setLoading(false);
    if (!res.ok) {
      setError(res.error || 'Could not load bridge routes.');
      // Fall back to an empty list — the form will show "No routes".
      _cached = { pairs: [] };
      setPairs([]);
      return;
    }
    const data = toPairs(res.data?.routes);
    _cached = { pairs: data };
    setPairs(data);
  };

  useEffect(() => {
    if (_cached) {
      setPairs(_cached.pairs);
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { pairs, loading, error, reload: load };
}
